import React, { useEffect, useState } from 'react'
import { styled, } from '@mui/system';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import { useParams } from 'react-router';
import PricingCard from './PricingCard';
import PaymentModal from 'Components/Payment';
import { toast } from 'react-toastify';
import { Backdrop, CircularProgress } from '@mui/material';
import { AppRegistrationOutlined, ArrowBack, Login, PlaylistAddCheck, InfoOutlined } from '@mui/icons-material';
import usePricing from 'Hooks/usePricing';
import { useNavigation } from 'Hooks/navigate';
import API from 'api';

const colorMain = "#fff"
const colorBackground = "linear-gradient(to right, #041e42, #78a8e6)"

const MainContainer = styled('div')({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: '5vh 0',
    background: colorBackground,
});

const Wrapper = styled('div')({
    textTransform: "capitalize",
    textAlign: 'center',
    '& h2': {
        color: colorMain,
        margin: '20px auto'
    },
    '& p': {
        color: 'gray',
        margin: '20px auto',
        fontSize: '16px'
    },
    '& span': {
        fontSize: '16px',
        fontWeight: 'bolder',
        color: colorMain
    }
});

const Pricing = () => {
    const { goBack, goToSignIn, goToAdvisorRegistration, goToClientRegistration } = useNavigation();
    const { roleType, subsType } = useParams();
    const { pricing, loading } = usePricing();

    const [selectedPackage, setSelectedPackage] = useState('')
    const [subsData, setSubsData] = useState([]);
    const [dataForPayment, setDataForPayment] = useState(null)
    const [isPaymentModalOpen, setisPaymentModalOpen] = useState(false)
    const [isTrialPackage, setIsTrialPackage] = useState(false);
    const [glossaryData, setGlossaryData] = useState([]);
    // Centralized toggle state management - key: packageId, value: billAnnually boolean
    const [toggleStates, setToggleStates] = useState({});


    const handleSelect = (selectedOffer) => {
        if (isTrialPackage) { // should not be able to select trial package
            return;
        }
        if (selectedPackage === selectedOffer) {
            setSelectedPackage("");
            setDataForPayment(null);
        } else {
            setSelectedPackage(selectedOffer)
        }
    }

    const handleToggleChange = (packageId, billAnnually) => {
        setToggleStates(prev => ({
            ...prev,
            [packageId]: billAnnually
        }));
    }

    const handleSubmit = () => {
        if (!selectedPackage) {
            toast.warning("Select an offer first")
            return;
        }
        // Get payment data with current toggle state
        const temp = subsData.find((item) => item.id === selectedPackage)
        if (temp) {
            // Get current toggle state, default to true (annually) if not set
            const billAnnually = toggleStates[selectedPackage] ?? true;
            const data = {
                templateId: temp.id || null,
                roleType: roleType,
                billAnnually: billAnnually,
                amount: billAnnually ? temp.annually : temp.monthly,
                currency: temp?.currency,
                offerSelected: temp?.name
            }
            setDataForPayment(data)
            setisPaymentModalOpen(true)
        }
    }

    const handleClose = () => {
        // Reset toggle state to default (annually) for the selected package
        if (selectedPackage) {
            setToggleStates(prev => ({
                ...prev,
                [selectedPackage]: true
            }));
        }
        setisPaymentModalOpen(false)
        setDataForPayment(null)
        setSelectedPackage("")
    }

    useEffect(() => {
        // Fetch glossary terms from backend and filter by roleType
        API.pricing.getGlossary()
            .then((res) => {
                const data = res.data?.data?.glossary || [];
                // Filter glossary based on roleType (advisor/client) or show if role is 'both'
                const filtered = data.filter(item => 
                    !item.role || item.role === 'both' || item.role === roleType
                );
                setGlossaryData(filtered);
            })
            .catch((err) => {
                console.error("Error fetching glossary:", err);
            });
    }, [roleType]);


    useEffect(() => {
        // setting data for the cards to display
        if (pricing && Object.keys(pricing).length > 0) {
            const _data = Object.entries(pricing[roleType]).filter(([label]) => label === subsType).map(([_, values]) => values)[0]

            const data = _data.map((item) => {
                if (item.subscriptionPackage === "Trial") {
                    setIsTrialPackage(true);
                }
                return ({
                    roleType: item.roleType,
                    id: item.id,
                    name: /* item.subscriptionType === "Trial" ? `Trial` : */ item.subscriptionPackage,
                    currency: item.Currency || "",
                    monthly: item.monthlyFee,
                    annually: item.annualFee,
                    billAnnually: true, // default to annual billing
                    badgeText: item.badgeText || null, // Dynamic badge text from backend
                    offers: item.SubscriptionTemplateItems.sort((a, b) => a.displayOrder - b.displayOrder).map((element) => ({
                        icon: element.effectiveLimit === "Contact Sales" ? "shield" : element.effectiveLimit !== "No" ? "check" : false,
                        desc: element.itemName || "",
                        info: element.effectiveLimit || "tooltipContactSales",
                        description: element.description || null, // Description for tooltips
                    })),
                    infoText: item.roleType === "client" ? [
                        `*The rates are exclusive of any applicable fees and taxes`,
                        `** Bring your own stakeholders`
                    ] : [
                        `*The rates are exclusive of any applicable fees and taxes`,
                    ],
                    displayOrder: item.displayOrder || null,
                })
            })
            console.log("Pricing subs data => ", data);
            setSubsData(data);
            
            // Initialize toggle states for all packages (default to annually)
            const initialToggles = {};
            data.forEach(item => {
                initialToggles[item.id] = true; // true = annually
            });
            setToggleStates(initialToggles);
        }
    }, [pricing, roleType, subsType])

    return (
        <>
            {loading ?
                <Backdrop open={loading}>
                    <CircularProgress style={{ color: "#fff" }} />
                </Backdrop> :
                <MainContainer>
                    <Container style={{ maxWidth: '90%' }} >
                        <Wrapper>
                            <h2>{subsType}</h2>
                        </Wrapper>
                        <div style={{ display: "flex", gap: "25px", justifyContent: "space-between", width: "100%", flexWrap: "wrap" }}>
                            <div style={{ display: "flex", gap: "25px", flexWrap: "wrap" }}>
                                <Button
                                    startIcon={<ArrowBack />}
                                    variant='outlined'
                                    sx={{ padding: "6px 40px", color: "#fff", borderColor: "#fff" }}
                                    onClick={() => goBack()}
                                >
                                    Back
                                </Button>
                                <Button
                                    startIcon={<Login />}
                                    variant='outlined'
                                    sx={{ padding: "6px 40px", color: "#fff", borderColor: "#fff" }}
                                    onClick={() => goToSignIn(roleType)}
                                >
                                    Sign-In
                                </Button>
                                <Button
                                    startIcon={<AppRegistrationOutlined />}
                                    variant='outlined'
                                    sx={{ padding: "6px 40px", color: "#fff", borderColor: "#fff" }}
                                    onClick={() => roleType === "advisor" ? goToAdvisorRegistration() : goToClientRegistration()}
                                >
                                    Register
                                </Button>
                            </div>
                            {!isTrialPackage && (
                                <div style={{ display: "flex", gap: "25px", flexWrap: "wrap" }}>
                                    <Button
                                        startIcon={<PlaylistAddCheck />}
                                        variant='outlined'
                                        onClick={handleSubmit}
                                        sx={{ padding: "6px 40px", color: "#fff", borderColor: "#fff" }}
                                    >Subscribe
                                    </Button>
                                </div>
                            )}
                        </div>
                        <div style={{ margin: "20px auto" }}>
                            <Grid container spacing={3} style={{ alignItems: "stretch" }}>
                                {!!subsData && subsData.length > 0 && subsData?.sort((a, b) => {
                                    if (!a?.displayOrder && !b?.displayOrder) return -1;
                                    return a.displayOrder - b.displayOrder
                                })?.filter((item) => item.roleType === roleType).map((item, index) => (
                                    <Grid key={index} item xs={12} sm={12} md={6} lg={4} style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', cursor: !isTrialPackage && "pointer" }}>
                                            <PricingCard
                                                selected={selectedPackage}
                                                OFFER={item}
                                                handleSelect={handleSelect}
                                                index={index}
                                                roleType={roleType}
                                                billAnnually={toggleStates[item.id] ?? true}
                                                onToggleChange={handleToggleChange}
                                            />
                                        </div>
                                    </Grid>
                                ))}
                            </Grid>
                        </div>

                        {/* Glossary Section */}
                        <div style={{ marginTop: '2rem', width: '100%' }}>
                            <details style={{
                                background: '#121826',
                                border: '1px solid #1f2937',
                                borderRadius: '.8rem',
                                padding: '.8rem 1rem',
                                color: '#e9edf5',
                            }}>
                                <summary style={{
                                    cursor: 'pointer',
                                    fontWeight: 700,
                                    fontSize: '1.1rem',
                                    padding: '.5rem 0',
                                }}>
                                    Glossary of Terms
                                </summary>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                                    gap: '.75rem',
                                    marginTop: '.75rem',
                                }}>
                                    {glossaryData.map((item, index) => (
                                        <div key={item.id || index} style={{
                                            padding: '.6rem .75rem',
                                            border: '1px dashed #1f2937',
                                            borderRadius: '.6rem',
                                            background: '#0f1420',
                                        }}>
                                            <h4 style={{ margin: '0 0 .25rem', fontSize: '1rem', color: '#e9edf5' }}>
                                                {item.term}
                                            </h4>
                                            <p style={{ margin: 0, color: '#b6c0d6', fontSize: '.95rem' }}>
                                                {item.description}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </details>
                        </div>
                    </Container>
                </MainContainer>
            }

            <PaymentModal data={dataForPayment} isPaymentModalOpen={isPaymentModalOpen} handleClose={handleClose} />
        </>
    );
}

export default Pricing;